import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Filter, Search, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import ShiftCard, { ShiftCardSkeleton } from "../../components/shifts/ShiftCard";
import EmptyState from "../../components/shifts/EmptyState";
import PageMeta from "../../components/seo/PageMeta";
import { US_STATES, SHIFT_TYPES, SPECIALTIES, SHIFTS_CTAS } from "../../data/marketplaceData";
import { useAuth } from "../../context/AuthContext";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

export default function BrowseShifts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [specialty, setSpecialty] = useState(searchParams.get("specialty") || "");
  const [stateF,    setStateF]    = useState(searchParams.get("state") || "");
  const [shiftType, setShiftType] = useState(searchParams.get("shift_type") || "");
  const [minRate,   setMinRate]   = useState(searchParams.get("min_rate") || "");

  const fetchShifts = useMemo(() => async () => {
    setLoading(true);
    const url = new URL(`${API}/public/shifts`, window.location.origin);
    if (specialty)  url.searchParams.set("specialty", specialty);
    if (stateF)     url.searchParams.set("state", stateF);
    if (shiftType)  url.searchParams.set("shift_type", shiftType);
    if (minRate)    url.searchParams.set("min_rate", minRate);
    url.searchParams.set("limit", "60");
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error("Shift service unavailable");
      const data = await r.json();
      if (!Array.isArray(data)) throw new Error("Unexpected response from shift service");
      setShifts(data);
      setFetchError(false);
    } catch {
      setShifts([]);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [specialty, stateF, shiftType, minRate]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  /* Sync URL params when filters change */
  useEffect(() => {
    const next = {};
    if (q)         next.q = q;
    if (specialty) next.specialty = specialty;
    if (stateF)    next.state = stateF;
    if (shiftType) next.shift_type = shiftType;
    if (minRate)   next.min_rate = minRate;
    setSearchParams(next, { replace: true });
  }, [q, specialty, stateF, shiftType, minRate, setSearchParams]);

  const filtered = useMemo(() => {
    if (!q) return shifts;
    const ql = q.toLowerCase();
    return shifts.filter(
      (s) =>
        (s.facility_name || "").toLowerCase().includes(ql) ||
        (s.specialty || "").toLowerCase().includes(ql) ||
        (s.city || "").toLowerCase().includes(ql) ||
        (s.state || "").toLowerCase().includes(ql)
    );
  }, [q, shifts]);

  const reset = () => { setQ(""); setSpecialty(""); setStateF(""); setShiftType(""); setMinRate(""); };

  const handleClaim = async (shift) => {
    if (!user || !token) {
      navigate(`/shifts/login?intent=claim&shift=${shift.id}`);
      return;
    }
    if (user.role !== "clinician") {
      toast.error("Only clinicians can claim shifts.", { description: "Sign in with a clinician account." });
      return;
    }
    try {
      const r = await fetch(`${API}/shifts/${shift.id}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "accept" }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success("Shift claimed!", { description: `${shift.facility_name} — ${new Date(shift.start_time).toLocaleString()}` });
      fetchShifts();
    } catch (err) {
      toast.error("Couldn't claim shift", { description: err?.message || "Try again." });
    }
  };

  const activeFilters = [specialty, stateF, shiftType, minRate].filter(Boolean).length;

  return (
    <ShiftsLayout>
      <PageMeta
        title="Browse Open PRN Shifts | RN, LPN, CNA, Allied & Therapy | TRITAL ShiftBridge™"
        description="Browse currently published PRN and per-diem healthcare shifts. Filter by specialty, state, shift type and pay rate. Availability depends on facility requests and clinician eligibility."
        canonical="https://www.tritalcare.com/shiftbridge/browse"
        ogImage="https://www.tritalcare.com/images/logos/trital-shiftbridge-logo-transparent-master.png"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.tritalcare.com/" },
              { "@type": "ListItem", "position": 2, "name": "ShiftBridge", "item": "https://www.tritalcare.com/shiftbridge" },
              { "@type": "ListItem", "position": 3, "name": "Browse Open PRN Shifts", "item": "https://www.tritalcare.com/shiftbridge/browse" }
            ]
          }
        ]}
      />

      {/* HERO + FILTER */}
      <section className="bg-trital-mesh border-b border-slate-200" data-testid="browse-hero">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-12 pb-8 lg:pt-16 lg:pb-10">
          <h1 className="font-display font-black text-4xl lg:text-[52px] leading-[1.02] text-slate-900 text-balance">
            Find PRN shifts that fit your schedule.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">{loading ? "Loading…" : `${filtered.length} shifts matched · ${shifts.length} live total`}</p>

          {/* Filter bar */}
          <div className="mt-8 grid lg:grid-cols-[1.6fr_1fr_1fr_1fr_0.8fr_auto] gap-2.5 bg-white border border-slate-200 rounded-3xl p-3 shadow-card">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.8} />
              <input
                type="search"
                placeholder="Search facility, city or specialty"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-10 pr-3 py-3 rounded-2xl border border-slate-200 bg-white text-[13.5px] focus:outline-none focus:border-trital-navy"
                data-testid="browse-search"
              />
            </div>
            <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="w-full px-3 py-3 rounded-2xl border border-slate-200 bg-white text-[13.5px] font-medium focus:outline-none focus:border-trital-navy" data-testid="browse-specialty">
              <option value="">All specialties</option>
              {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={stateF} onChange={(e) => setStateF(e.target.value)} className="w-full px-3 py-3 rounded-2xl border border-slate-200 bg-white text-[13.5px] font-medium focus:outline-none focus:border-trital-navy" data-testid="browse-state">
              <option value="">All states</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={shiftType} onChange={(e) => setShiftType(e.target.value)} className="w-full px-3 py-3 rounded-2xl border border-slate-200 bg-white text-[13.5px] font-medium focus:outline-none focus:border-trital-navy" data-testid="browse-shift-type">
              <option value="">Any shift type</option>
              {SHIFT_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              type="number"
              min="0"
              placeholder="Min $/hr"
              value={minRate}
              onChange={(e) => setMinRate(e.target.value)}
              className="w-full px-3 py-3 rounded-2xl border border-slate-200 bg-white text-[13.5px] font-medium focus:outline-none focus:border-trital-navy tabular-nums"
              data-testid="browse-min-rate"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={fetchShifts}
                className="px-4 py-3 rounded-2xl bg-trital-navy text-white text-[13px] font-semibold hover:bg-trital-navy-deep inline-flex items-center gap-1.5"
                data-testid="browse-apply"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2} /> Apply
              </button>
            </div>
          </div>

          {/* Active filters */}
          {activeFilters > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="browse-active-filters">
              <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold inline-flex items-center gap-1"><Filter className="w-3 h-3" strokeWidth={1.8} /> Active</span>
              {[
                specialty && ["specialty", specialty, () => setSpecialty("")],
                stateF && ["state", stateF, () => setStateF("")],
                shiftType && ["shift", shiftType, () => setShiftType("")],
                minRate && ["min $", `≥ $${minRate}/hr`, () => setMinRate("")],
              ].filter(Boolean).map(([k, v, clr], i) => (
                <button
                  key={i}
                  type="button"
                  onClick={clr}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-trital-navy/10 border border-trital-navy/20 text-trital-navy text-[12px] font-semibold hover:bg-trital-navy/15"
                >
                  {k}: {v} <X className="w-3 h-3" strokeWidth={2} />
                </button>
              ))}
              <button type="button" onClick={reset} className="text-[12px] font-semibold text-slate-500 hover:text-trital-navy">Reset all</button>
            </div>
          )}
        </div>
      </section>

      {/* RESULTS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 lg:py-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="browse-loading">
            {Array.from({ length: 9 }).map((_, i) => <ShiftCardSkeleton key={i} index={i} />)}
          </div>
        ) : fetchError ? (
          <div role="alert" data-testid="browse-api-unavailable" className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-7">
            <h2 className="font-display font-bold text-xl text-amber-950">Live shift availability is temporarily unavailable</h2>
            <p className="mt-2 text-sm text-amber-900">We could not connect to the shift service. Please retry or contact the PRN workforce team at shifts@tritalcare.com. We will not show demonstration shifts as real jobs.</p>
            <button type="button" onClick={fetchShifts} className="mt-4 px-5 py-2.5 rounded-xl bg-trital-navy text-white text-sm font-bold">Retry live shift search</button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={shifts.length === 0 ? "No shifts are live right now." : "No shifts match those filters."}
            message={shifts.length === 0 ? "No shifts match the currently published inventory. You can register a clinician profile for future opportunities." : "Try widening the rate floor or removing a specialty filter."}
            action={shifts.length === 0 ? "Sign up for shift alerts" : "Reset filters"}
            actionTo={shifts.length === 0 ? SHIFTS_CTAS.clinicianSignup : null}
            onAction={shifts.length === 0 ? null : reset}
            testid="browse-empty"
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="browse-grid">
            {filtered.map((s, i) => (
              <ShiftCard key={s.id} shift={s} index={i} onClaim={user ? handleClaim : null} />
            ))}
          </div>
        )}
      </section>

      {/* CTA BAND */}
      {!user && (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 text-center">
            <h2 className="font-display font-black text-3xl lg:text-4xl text-slate-900">Ready to claim a shift?</h2>
            <p className="mt-3 text-slate-600 max-w-[52ch] mx-auto">Free clinician signup. Credentialing timelines and booking eligibility vary by facility and specialty.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={SHIFTS_CTAS.clinicianSignup} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-[14px] font-semibold btn-lift hover:bg-trital-red-deep" data-testid="browse-bottom-signup">
                Create clinician account
              </Link>
              <Link to={SHIFTS_CTAS.login} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-slate-700 text-[14px] font-semibold hover:border-trital-navy hover:bg-white">
                I already have an account
              </Link>
            </div>
          </div>
        </section>
      )}
    </ShiftsLayout>
  );
}
